import { useEffect, useRef, useState, forwardRef } from "react";
import { Box } from "@mui/material";
import { ResizableBox } from "react-resizable";
import PdfViewer from "./PDFViewer.js";
import useGetSignedUrl from "../../api/signedurl";
import { stableDocumentKeyFromUrl } from "../../utils/documentIdentity.js";

// Custom drag handle — visible grippy bar between panels
const DragHandle = forwardRef((props, ref) => {
    const { handleAxis, ...restProps } = props;
    return (
        <div
            ref={ref}
            {...restProps}
            style={{
                position: 'absolute',
                right: -4,
                top: 0,
                bottom: 0,
                width: 8,
                cursor: 'col-resize',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <div style={{
                width: 4,
                height: 40,
                borderRadius: 2,
                background: 'var(--color-gray-400, #9ca3af)',
                opacity: 0.6,
                transition: 'opacity 0.15s, background 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.background = 'var(--color-accent-500, #14b8a6)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.6'; e.currentTarget.style.background = 'var(--color-gray-400, #9ca3af)'; }}
            />
        </div>
    );
});

const PDFSidePanel = ({ leftChild, highlightedTxn, fileUrl, pdfBucket = null, pdfKey = null, documentName = null, scanVariant = 'Ocrolus', resetKey = 0, displayPage = null }) => {
    const containerRef = useRef(null);
    const [panelWidth, setPanelWidth] = useState(400);
    const [navKey, setNavKey] = useState(0);

    // Callers may hand us a ready-signed URL (fileUrl) OR a bucket/key to sign
    // ourselves. The internal-signing path lets simpler consumers (e.g. the
    // check-image review panel, Trello #800) get the shared viewer without
    // duplicating the useGetSignedUrl glue. When fileUrl is supplied we don't
    // sign — existing callers are unaffected.
    const { data: signedUrl } = useGetSignedUrl(pdfBucket, pdfKey, !!(pdfBucket && pdfKey && !fileUrl));
    const effectiveUrl = fileUrl || signedUrl;

    // Measure the actual container width (not window.innerWidth) so the
    // resizable panel respects the nav sidebar and chat panel.
    useEffect(() => {
        const measure = () => {
            if (containerRef.current) {
                setPanelWidth(Math.floor(containerRef.current.offsetWidth * 0.6));
            }
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, []);

    const viewerBbox = highlightedTxn?.bbox;
    // Page to show: a highlighted transaction's page takes precedence; otherwise a
    // caller-supplied displayPage (e.g. Step-1 doc-split thumbnail focus) drives the
    // viewer. Falls back to page 1 when neither is present.
    const pageNumber = Number(
        highlightedTxn ? (highlightedTxn.page_number || highlightedTxn.start_page) : (displayPage || 1)
    );

    // Re-navigate on a genuinely new document, NOT on a re-signed URL for the same
    // one. The signed URL is re-minted on a timer, and keying this effect on the raw
    // URL bumped navKey every time — which told the viewer to navigate, landing the
    // reviewer back on page 1 mid-review (Trello IYmLT1Pl).
    const stableUrlKey = stableDocumentKeyFromUrl(effectiveUrl);

    useEffect(() => {
        setNavKey(prevKey => prevKey + 1);
    }, [pageNumber, viewerBbox, stableUrlKey]);

    // Compute max width from actual container
    const maxWidth = containerRef.current ? containerRef.current.offsetWidth - 100 : 9999;

    // When no file is selected (or the signed URL hasn't resolved yet), give the
    // grid the full width.
    if (!effectiveUrl) {
        return (
            <Box
                ref={containerRef}
                sx={{
                    display: 'flex',
                    flex: 1,
                    height: '100%',
                    minHeight: 0,
                    minWidth: 0,
                    overflow: 'hidden',
                    width: '100%',
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        width: '100%',
                        minHeight: 0,
                        minWidth: 0,
                        overflow: 'hidden',
                        padding: 1,
                    }}
                >
                    {leftChild}
                </Box>
            </Box>
        );
    }

    return (
        <Box
            ref={containerRef}
            sx={{
                display: 'flex',
                flex: 1,
                height: '100%',
                minHeight: 0,
                overflow: 'hidden',
                width: '100%',
            }}
        >
            <ResizableBox
                width={panelWidth}
                height={Infinity}
                minConstraints={[200, Infinity]}
                maxConstraints={[maxWidth, Infinity]}
                axis="x"
                resizeHandles={['e']}
                handle={<DragHandle />}
                style={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRight: '1px solid var(--color-gray-200, #e5e7eb)',
                    height: '100%',
                    flexShrink: 0,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        width: '100%',
                        minHeight: 0,
                        overflowY: 'auto',
                        overflowX: 'auto',
                        padding: 1,
                    }}
                >
                    {leftChild}
                </Box>
            </ResizableBox>
            <Box sx={{
                flex: 1,
                height: '100%',
                minHeight: 0,
                minWidth: 0,
                overflow: 'hidden'
            }}>
                <PdfViewer
                    url={effectiveUrl}
                    // Save-as filename. Split statement PDFs are keyed
                    // `.../document_{n}.pdf`, so without this the viewer's
                    // URL-derived fallback offers "document 1" instead of the
                    // reviewer's own upload name (Trello iX9j4CR6).
                    documentName={documentName}
                    boundingBox={viewerBbox}
                    transactionSearch={{
                        descr: highlightedTxn ? highlightedTxn.description : null,
                        amount: highlightedTxn ? highlightedTxn.amount : null,
                        posting_date: highlightedTxn ? highlightedTxn.posting_date : null
                    }}
                    displayPage={pageNumber}
                    navigationKey={navKey}
                    resetKey={resetKey}
                />
            </Box>
        </Box>
    );
};

export default PDFSidePanel;
